import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import handwritingFontUrl from '../../assets/fonts/DancingScript.ttf?url'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { RadioGroup, RadioGroupItem } from '../ui/radio-group'
import { FRAME_RATIO, isDrawingAcceptable, isHandwritingReady, normalizePoint, type Handwriting, type Strokes } from './signature-utils'

export { isHandwritingReady, type Handwriting }

const FONT_FAMILY = 'Buildtory Handwriting'
let fontLoading: Promise<void> | null = null

function loadHandwritingFont(): Promise<void> {
    if (!fontLoading && typeof FontFace !== 'undefined') {
        const face = new FontFace(FONT_FAMILY, `url(${handwritingFontUrl})`)
        fontLoading = face.load().then((f) => {
            document.fonts.add(f)
        }).catch(() => undefined)
    }
    return fontLoading ?? Promise.resolve()
}

/**
 * The handwritten signature (spec 2026-10-05-05 §3): drawn with a finger, a
 * stylus or a mouse in a frame, or typed and shown in a handwriting font. The
 * typed way is the keyboard-only alternative. Only the geometry of a drawing
 * leaves the page: points normalized to the frame, never their timing nor
 * pressure. The server re-renders the image either way.
 */
export function SignaturePad({ value, onChange, defaultTyped = '' }: {
    value: Handwriting | null
    onChange: (h: Handwriting | null) => void
    /** Prefills the typed name (the signer's, when known). The typed mode is the default one. */
    defaultTyped?: string
}) {
    const { t } = useTranslation('signatures')
    const [mode, setMode] = useState<'drawn' | 'typed'>(value?.kind ?? 'typed')
    const [strokes, setStrokes] = useState<Strokes>(value?.kind === 'drawn' ? value.strokes : [])
    const [typed, setTyped] = useState(value?.kind === 'typed' ? value.typed : defaultTyped)
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const drawing = useRef(false)
    // The strokes being drawn, read by the pointer handlers between renders.
    const live = useRef<Strokes>(strokes)

    useEffect(() => {
        void loadHandwritingFont()
    }, [])

    const redraw = useCallback((all: Strokes) => {
        const canvas = canvasRef.current
        if (!canvas) return
        const ctx = canvas.getContext('2d')
        if (!ctx) return
        const { width, height } = canvas
        ctx.clearRect(0, 0, width, height)
        ctx.lineWidth = Math.max(2, width / 250)
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
        ctx.strokeStyle = getComputedStyle(canvas).color || '#111'
        for (const s of all) {
            if (s.length === 0) continue
            ctx.beginPath()
            ctx.moveTo(s[0][0] * width, s[0][1] * height)
            if (s.length === 1) ctx.lineTo(s[0][0] * width + 0.1, s[0][1] * height)
            for (let i = 1; i < s.length; i++) ctx.lineTo(s[i][0] * width, s[i][1] * height)
            ctx.stroke()
        }
    }, [])

    // Keep the canvas backing store at the element's pixel size.
    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas || mode !== 'drawn') return
        const fit = () => {
            const rect = canvas.getBoundingClientRect()
            const ratio = window.devicePixelRatio || 1
            canvas.width = Math.max(1, Math.round(rect.width * ratio))
            canvas.height = Math.max(1, Math.round((rect.width / FRAME_RATIO) * ratio))
            redraw(strokes)
        }
        fit()
        if (typeof ResizeObserver === 'undefined') return
        const ro = new ResizeObserver(fit)
        ro.observe(canvas)
        return () => ro.disconnect()
    }, [mode, redraw, strokes])

    const emitDrawn = (next: Strokes) => {
        live.current = next
        setStrokes(next)
        onChange(next.length ? { kind: 'drawn', strokes: next } : null)
    }

    const point = (e: React.PointerEvent<HTMLCanvasElement>) =>
        normalizePoint(e.clientX, e.clientY, e.currentTarget.getBoundingClientRect())

    const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
        e.preventDefault()
        e.currentTarget.setPointerCapture?.(e.pointerId)
        drawing.current = true
        const next: Strokes = [...live.current, [point(e)]]
        live.current = next
        redraw(next)
    }
    const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
        if (!drawing.current) return
        const next = live.current.slice()
        const last = next[next.length - 1]
        if (!last) return
        next[next.length - 1] = [...last, point(e)]
        live.current = next
        redraw(next)
    }
    const onPointerUp = () => {
        if (!drawing.current) return
        drawing.current = false
        emitDrawn(live.current)
    }

    const switchMode = (m: string) => {
        const next = m === 'typed' ? 'typed' : 'drawn'
        setMode(next)
        if (next === 'typed') onChange(typed.trim() ? { kind: 'typed', typed } : null)
        else onChange(strokes.length ? { kind: 'drawn', strokes } : null)
    }

    return (
        <div className="space-y-3" data-testid="signature-pad">
            <RadioGroup value={mode} onValueChange={switchMode} className="flex gap-4" aria-label={t('pad.mode_label')}>
                <div className="flex items-center gap-2">
                    <RadioGroupItem value="drawn" id="sig-mode-drawn" data-testid="signature-mode-drawn" />
                    <Label htmlFor="sig-mode-drawn">{t('pad.mode_drawn')}</Label>
                </div>
                <div className="flex items-center gap-2">
                    <RadioGroupItem value="typed" id="sig-mode-typed" data-testid="signature-mode-typed" />
                    <Label htmlFor="sig-mode-typed">{t('pad.mode_typed')}</Label>
                </div>
            </RadioGroup>

            {mode === 'drawn' ? (
                <div className="space-y-2">
                    <canvas
                        ref={canvasRef}
                        className="w-full touch-none rounded-md border border-dashed border-border bg-background text-foreground cursor-crosshair"
                        style={{ aspectRatio: `${FRAME_RATIO} / 1` }}
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={onPointerUp}
                        onPointerCancel={onPointerUp}
                        onPointerLeave={onPointerUp}
                        role="img"
                        aria-label={t('pad.canvas_label')}
                        data-testid="signature-canvas"
                    />
                    <div className="flex items-center justify-between gap-2">
                        <p className="text-xs text-muted-foreground">
                            {strokes.length > 0 && !isDrawingAcceptable(strokes) ? t('pad.too_short') : t('pad.draw_hint')}
                        </p>
                        <Button type="button" variant="outline" size="sm" onClick={() => { emitDrawn([]); redraw([]) }} data-testid="signature-clear">
                            {t('pad.clear')}
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="space-y-2">
                    <Label htmlFor="sig-typed">{t('pad.typed_label')}</Label>
                    <Input
                        id="sig-typed"
                        value={typed}
                        maxLength={100}
                        autoComplete="name"
                        onChange={(e) => {
                            setTyped(e.target.value)
                            onChange(e.target.value.trim() ? { kind: 'typed', typed: e.target.value } : null)
                        }}
                        data-testid="signature-typed-input"
                    />
                    <div
                        className="flex min-h-20 items-center justify-center rounded-md border border-dashed border-border px-4 text-4xl text-foreground"
                        style={{ fontFamily: `"${FONT_FAMILY}", cursive` }}
                        aria-hidden="true"
                        data-testid="signature-typed-preview"
                    >
                        {typed}
                    </div>
                </div>
            )}
        </div>
    )
}
